import { api } from '@/lib/api'
import type {
  CancelContractRequest,
  Contract,
  ContractListResponse,
  CreateContractRequest,
  FormSchema,
  OverrideStageRequest,
  UpdateSectionRequest,
} from './types'

export type ContractPickerOption = {
  id: number
  /** Numer umowy, np. UMW/2026/64AA00C1 */
  code: string
  /** Imię + nazwisko zamawiajacego (party_role='orderer') */
  customerName: string
  /** Aktualny etap, np. "MONTAZ" */
  stageName: string
}

const BASE = '/api/contracts'

export async function listContracts(stage?: string): Promise<ContractListResponse> {
  const params: Record<string, string> = {}
  if (stage) params.stage = stage
  const res = await api.get<ContractListResponse>(BASE, { params })
  return res.data
}

export async function getContract(id: number): Promise<Contract> {
  const res = await api.get<Contract>(`${BASE}/${id}`)
  return res.data
}

export async function getFormSchema(objectType: string): Promise<FormSchema> {
  const res = await api.get<FormSchema>(`/api/forms/${objectType}`)
  return res.data
}

export async function getConfigurationFormSchema(configurationCode: string): Promise<FormSchema> {
  const res = await api.get<FormSchema>(`/api/forms/configuration/${configurationCode}`)
  return res.data
}

export async function updateSection(
  id: number,
  sectionCode: string,
  payload: UpdateSectionRequest,
): Promise<Contract> {
  const res = await api.patch<Contract>(`${BASE}/${id}/sections/${sectionCode}`, payload)
  return res.data
}

export async function completeStage(id: number): Promise<Contract> {
  const res = await api.post<Contract>(`${BASE}/${id}/complete-stage`)
  return res.data
}

export async function overrideStage(
  id: number,
  payload: OverrideStageRequest,
): Promise<Contract> {
  const res = await api.post<Contract>(`${BASE}/${id}/override-stage`, payload)
  return res.data
}

export async function cancelContract(
  id: number,
  payload: CancelContractRequest,
): Promise<void> {
  await api.post(`${BASE}/${id}/cancel`, payload)
}

export async function createContract(payload: CreateContractRequest): Promise<Contract> {
  const res = await api.post<Contract>(BASE, payload)
  return res.data
}

export async function changeSalesPerson(
  id: number,
  salesPersonId: number | null,
): Promise<Contract> {
  const res = await api.patch<Contract>(`${BASE}/${id}/sales-person`, { salesPersonId })
  return res.data
}

/**
 * Wyszukuje umowy po frazie (ILIKE po numerze umowy lub nazwisku zamawiajacego).
 * Zwraca max 50 wynikow (limit egzekwowany po stronie backendu).
 * Pusty/blank query zwraca pusta tablice bez uderzania w API.
 */
export async function searchContracts(query: string): Promise<ContractPickerOption[]> {
  if (!query.trim()) return []
  const res = await api.get<ContractListResponse>(BASE, { params: { search: query, limit: 50 } })
  return res.data.items.map((c) => ({
    id: c.id,
    code: c.code,
    customerName: c.ordererName ?? '?',
    stageName: c.currentStageCode,
  }))
}
